// Pure helpers for the bulk upload in UploadFile.vue: kept free of Vue / axios so they can be
// unit-tested directly.

export const MAX_FILES_PER_UPLOAD = 50;
export const UPLOAD_CONCURRENCY = 3;

// The template's `accept=` only hints the OS picker; "All files" lets anything through. jpeg/tif
// are included because the backend's mimeTypeByFileName already serves them.
export const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'avif'];

const MAX_NAMES_LISTED = 5;

// v-file-input emits a File (single), a File[] (multiple) or null/[] when cleared.
export const toFileArray = (input) => {
  const list = Array.isArray(input) ? input : [input];
  return list.filter((file) => file && file.size && file.name);
};

const extensionOf = (name) => {
  const match = (name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
};

export const partitionByExtension = (files) => {
  const accepted = [];
  const rejected = [];
  files.forEach((file) => {
    if (ACCEPTED_EXTENSIONS.includes(extensionOf(file.name))) {
      accepted.push(file);
    } else {
      rejected.push(file);
    }
  });
  return { accepted, rejected };
};

// Runs worker(item, index) with at most `limit` in flight and resolves (never rejects) to the
// results in input order. Once shouldStop() is true, items not yet started are not attempted.
export const runWithConcurrency = async (items, limit, worker, shouldStop = () => false) => {
  const results = new Array(items.length);
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      if (shouldStop()) {
        results[index] = { ok: false, skipped: true };
      } else {
        try {
          // eslint-disable-next-line no-await-in-loop
          results[index] = await worker(items[index], index);
        } catch (error) {
          results[index] = { ok: false, error };
        }
      }
    }
  };
  const lanes = Array.from({ length: Math.min(limit, items.length) }, lane);
  await Promise.all(lanes);
  return results;
};

const listNames = (names) => {
  const shown = names.slice(0, MAX_NAMES_LISTED).join(', ');
  const hidden = names.length - MAX_NAMES_LISTED;
  return hidden > 0 ? `${shown} and ${hidden} more` : shown;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// Joins sentences, adding a period where the previous one has no closing punctuation.
const joinSentences = (sentences) => sentences.reduce((text, sentence) => {
  if (!text) return sentence;
  return /[.!?]$/.test(text) ? `${text} ${sentence}` : `${text}. ${sentence}`;
}, '');

// results: [{ ok, name, skipped? }]; rejected: File[] refused by type before uploading;
// stopReason: why a batch stopped early ('permission' | 'session'), if it did.
export const summarizeResults = (results, rejected = [], stopReason = 'permission') => {
  const succeeded = results.filter((r) => r.ok);
  const skipped = results.filter((r) => !r.ok && r.skipped);
  const failed = results.filter((r) => !r.ok && !r.skipped);
  const sentences = [];
  let level = 'flashInfo';

  if (results.length) {
    if (!failed.length && !skipped.length) {
      sentences.push(succeeded.length === 1 ? 'File uploaded!' : `${succeeded.length} files uploaded!`);
    } else if (!succeeded.length) {
      level = 'flashError';
      if (failed.length) {
        sentences.push(failed.length === 1
          ? 'Unable to upload file.'
          : `Unable to upload ${failed.length} files: ${listNames(failed.map((r) => r.name))}`);
      }
    } else {
      level = 'flashWarn';
      const failedList = failed.length ? ` Failed: ${listNames(failed.map((r) => r.name))}` : '';
      sentences.push(`${succeeded.length} of ${plural(results.length, 'file')} uploaded.${failedList}`);
    }
  }
  const notAttempted = `${plural(skipped.length, 'file')} not attempted.`;
  if (stopReason === 'session') {
    sentences.push(skipped.length ? `Your session expired; ${notAttempted}` : 'Your session expired.');
  } else if (skipped.length) {
    sentences.push(`Stopped after a permission error; ${notAttempted}`);
  }
  if (rejected.length) {
    if (level === 'flashInfo') level = 'flashWarn';
    sentences.push(`Skipped (unsupported type): ${listNames(rejected.map((f) => f.name))}`);
  }
  return { level, message: joinSentences(sentences) };
};

// A pick-list multi-attach stores one file against many records (metadata.parentObjectId is an
// array). parentObjectId: this dialog's record, or a comma joined pick list group.
// Returns how many of the file's records are in view (removing) and how many are not (others),
// or null for a file stored against a single record.
export const sharedFileCounts = (file, parentObjectId) => {
  const stored = file && file.metadata && file.metadata.parentObjectId;
  if (!Array.isArray(stored)) return null;
  const inView = new Set(String(parentObjectId || '').split(',').map((id) => id.trim()));
  const removing = stored.filter((id) => inView.has(String(id))).length;
  return { removing, others: stored.length - removing, total: stored.length };
};

// Only then does deleting it from this view leave it on other records.
export const isSharedElsewhere = (file, parentObjectId) => {
  const counts = sharedFileCounts(file, parentObjectId);
  return !!counts && counts.removing > 0 && counts.others > 0;
};

const recordsInView = (removing) => (removing === 1 ? 'this record' : `these ${removing} records`);

export const alsoAttachedMessage = (others) => `This file is also attached to ${plural(others, 'other record')}.`;

export const removeHereLabel = (removing) => `Remove from ${recordsInView(removing)}`;

export const removeFromAllConfirmMessage = (total) => `Are you sure? This deletes the file from all ${total} records. This can't be undone.`;
